import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, setToken, getToken } from '../lib/adminApi';

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (getToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email and password are required.'); return; }
    setBusy(true);
    try {
      const res = await adminApi.login(email, password);
      setToken(res.token);
      toast.success('Welcome back.');
      nav('/admin', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid credentials.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="admin-login-page">
      <div className="hidden lg:block relative bg-[#eb5381]">
        <img src="https://images.pexels.com/photos/11742213/pexels-photo-11742213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000" alt="" className="w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#eb5381]/40 to-transparent" />
        <div className="absolute top-10 left-10 text-white">
          <div className="font-signature text-5xl">findyourmua</div>
          <div className="text-[10px] uppercase tracking-[0.3em] mt-1">Concierge · Admin</div>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-16">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381] inline-flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Admin Sign In
          </div>
          <h1 className="mt-3 font-serif text-5xl tracking-tight">Welcome back.</h1>
          <p className="mt-3 text-sm text-neutral-600">Sign in to manage your artist directory.</p>

          <div className="mt-12 space-y-7">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-600 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid="admin-login-email"
                placeholder="you@email.com"
                className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#eb5381] outline-none px-0 py-3 text-base"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-600 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                data-testid="admin-login-password"
                placeholder="••••••••"
                className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#eb5381] outline-none px-0 py-3 text-base"
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-60 text-white px-8 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign In
          </button>
          <p className="mt-6 text-xs text-neutral-500">Forgot password? Edit <code>ADMIN_PASSWORD</code> in <code>backend/.env</code>.</p>
        </form>
      </div>
    </div>
  );
}
